import { makeAutoObservable, observable } from "mobx";

const DEFAULT_PAGE_SIZE = 5;

const emptySummary = () => ({
  targetTotal: null,
  actualTotal: null,
  gapTotal: null,
});

const rowKeyOf = (disclosureId, year) => `${disclosureId}-${year}`;

class MetricDetailPanelState {
  pivot = null;
  rows = [];
  total = 0;
  summary = emptySummary();
  page = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  search = "";
  statuses = [];
  drillOrgIds = [];
  loading = false;
  hasLoadedOnce = false;

  constructor(pivot) {
    this.pivot = pivot;
    this.loading = true;
    makeAutoObservable(this);
  }

  setResult = ({ rows, total, summary, page, pageSize }) => {
    this.rows = rows ?? [];
    this.total = Number(total ?? 0);
    this.summary = summary ?? emptySummary();
    this.page = Number(page ?? this.page);
    this.pageSize = Number(pageSize ?? this.pageSize);
    this.hasLoadedOnce = true;
  };

  setLoading = (value) => {
    this.loading = Boolean(value);
  };

  setSearch = (value) => {
    this.search = value ?? "";
    this.page = 1;
  };

  setStatuses = (value) => {
    this.statuses = Array.isArray(value) ? value : [];
    this.page = 1;
  };

  setDrillOrgIds = (value) => {
    this.drillOrgIds = Array.isArray(value) ? value : [];
    this.page = 1;
  };

  setPageSize = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) return;
    this.pageSize = n;
    this.page = 1;
  };

  setPage = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) return;
    this.page = n;
  };
}

class DashboardMetricDetailStore {
  expandedKeys = observable.set();
  panels = observable.map();

  constructor() {
    makeAutoObservable(this, {
      expandedKeys: observable,
      panels: observable,
    });
  }

  // ── expand/collapse ──────────────────────────────────────────────

  toggle = ({ disclosureId, year }) => {
    const key = rowKeyOf(disclosureId, year);
    if (this.expandedKeys.has(key)) {
      this.collapse(key);
    } else {
      this.expand({ disclosureId, year });
    }
  };

  expand = ({ disclosureId, year }) => {
    const key = rowKeyOf(disclosureId, year);
    this.expandedKeys.add(key);
    this.panels.set(key, new MetricDetailPanelState({ disclosureId, year }));
  };

  collapse = (key) => {
    this.expandedKeys.delete(key);
    this.panels.delete(key);
  };

  collapseAll = () => {
    this.expandedKeys.clear();
    this.panels.clear();
  };

  isExpanded = (key) => this.expandedKeys.has(key);

  getPanel = (key) => this.panels.get(key);
}

export { rowKeyOf };
export const dashboardMetricDetailStore = new DashboardMetricDetailStore();
